import React from "react";
import { Search, Filter, ArrowUpDown, X, ChevronDown } from "@semo/icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../primitives/popover";
import { Checkbox } from "../../primitives/checkbox";
import { cn } from "../../utils";
import { STATUS_FILTER_OPTIONS } from "../project-card/utils";

export interface ProjectFilterSectionProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  filterStatuses: string[];
  onFilterStatusesChange: (statuses: string[]) => void;
  sortBy: string;
  onSortByChange: (value: any) => void;
  recommendedKeywords?: string[];
  placeholder?: string;
  className?: string;
  sortOptions?: { key: string; label: string }[];
  filterOptions?: { key: string; label: string }[];
  filterLabel?: string;
}

export const ProjectFilterSection: React.FC<ProjectFilterSectionProps> = ({
  searchQuery,
  onSearchChange,
  filterStatuses,
  onFilterStatusesChange,
  sortBy,
  onSortByChange,
  recommendedKeywords = [],
  placeholder = "프로젝트명, 지역, 태그 검색...",
  className,
  sortOptions = [
    { key: "latest", label: "최신순" },
    { key: "deadline", label: "마감일순" },
    { key: "applicants", label: "지원자순" },
  ],
  filterOptions = STATUS_FILTER_OPTIONS,
  filterLabel = "상태",
}) => {
  return (
    <div
      className={cn(
        "bg-white rounded-2xl border border-gray-100 p-5 mb-6 shadow-sm space-y-4",
        className,
      )}
    >
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder={placeholder}
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/50 text-gray-900 transition-all font-medium placeholder:text-gray-400"
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-3">
          {/* filterOptions 가 비어있으면 상태 필터 드롭다운을 숨긴다 (소비자가 자체 UI로 대체) */}
          {filterOptions.length > 0 && (
            <Popover>
              <PopoverTrigger asChild>
                <button className="relative flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-100 rounded-xl text-gray-700 text-sm font-semibold shadow-sm hover:border-primary/20 transition-all">
                  <Filter className="w-4 h-4 text-primary" />
                  {filterLabel}{" "}
                  {filterStatuses.length > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-primary-foreground text-2xs rounded-full flex items-center justify-center font-semibold shadow-sm">
                      {filterStatuses.length}
                    </span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-4" align="end">
                <div className="space-y-4">
                  <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                    <h3 className="font-semibold text-gray-900">
                      {filterLabel} 필터
                    </h3>
                    <button
                      onClick={() => onFilterStatusesChange([])}
                      className="text-xs text-primary font-semibold hover:underline"
                    >
                      초기화
                    </button>
                  </div>
                  <div className="space-y-2">
                    {filterOptions.map(({ key, label }) => (
                      <label
                        key={key}
                        className="flex items-center gap-2 cursor-pointer group p-1 hover:bg-gray-50 rounded-lg transition-colors"
                      >
                        <Checkbox
                          checked={filterStatuses.includes(key)}
                          onCheckedChange={(checked) =>
                            onFilterStatusesChange(
                              checked
                                ? [...filterStatuses, key]
                                : filterStatuses.filter((s) => s !== key),
                            )
                          }
                        />
                        <span className="text-sm text-gray-600 group-hover:text-gray-900 font-medium">
                          {label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          )}
          <Popover>
            <PopoverTrigger asChild>
              <button className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-100 rounded-xl text-gray-700 text-sm font-semibold shadow-sm hover:border-primary/20 transition-all">
                <ArrowUpDown className="w-4 h-4 text-primary" />
                정렬 <ChevronDown className="w-3 h-3 text-gray-400" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-48 p-2" align="end">
              {sortOptions.map((s) => (
                <button
                  key={s.key}
                  onClick={() => onSortByChange(s.key)}
                  className={cn(
                    "w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all",
                    sortBy === s.key
                      ? "bg-primary/5 text-primary font-semibold shadow-sm"
                      : "text-gray-600 hover:bg-gray-50 font-medium",
                  )}
                >
                  {s.label}
                </button>
              ))}
            </PopoverContent>
          </Popover>
        </div>
      </div>
      {recommendedKeywords.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100/50">
          <span className="text-2xs font-semibold text-gray-300 py-1.5 mr-1 uppercase tracking-wider">
            추천 검색어
          </span>
          {recommendedKeywords.map((keyword) => (
            <button
              key={keyword}
              onClick={() =>
                onSearchChange(searchQuery === keyword ? "" : keyword)
              }
              className={cn(
                "text-xs px-4 py-1.5 rounded-full border transition-all font-semibold shadow-sm",
                searchQuery === keyword
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-white border-gray-100 text-gray-400 hover:border-primary/30 hover:text-primary",
              )}
            >
              {keyword}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
